import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { useTeam } from 'app/frontend/base/config/context/team'
import dayjs from 'dayjs'
import { orderBy } from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { LootContext } from '../context'
dayjs.extend(RelativeTime)

type Scope = 'raid' | 'dungeon'

type Props = {
  teamWishlist?: wowaudit.TeamWishlist
  open: boolean
  setOpen: (open: boolean) => void
}

const TeamAgeModal = ({ teamWishlist, open, setOpen }: Props) => {
  const { team } = useTeam()
  const { selectedSeason } = useContext(LootContext)
  const [scope, setScope] = useState<Scope>('raid')
  const hasDungeons = (selectedSeason.keystone_dungeons || []).length > 0

  useEffect(() => {
    if (open) setScope('raid')
  }, [open])

  const withSortedSpecs = useMemo(
    () =>
      orderBy(
        teamWishlist?.metadata.map((character) => {
          const specData = character.specs.map((spec) => {
            const { age, report_url } = spec[scope]
            const secondsSinceEpoch = age ? dayjs(age).unix() : 0
            const difference = dayjs().diff(dayjs(age), 'day')

            return {
              ...spec,
              age,
              report_url,
              difference,
              secondsSinceEpoch,
            }
          })

          return {
            ...character,
            specs: orderBy(specData, (s) => s.secondsSinceEpoch, 'desc'),
          }
        }),
        (c) => c.specs[0]?.secondsSinceEpoch ?? 0,
        'desc',
      ),
    [teamWishlist?.metadata, scope],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Time since last update</DialogTitle>
        </DialogHeader>
        {hasDungeons && (
          <Tabs
            value={scope}
            onValueChange={(value) => setScope(value as Scope)}
          >
            <TabsList>
              <TabsTrigger value='raid'>Raids</TabsTrigger>
              <TabsTrigger value='dungeon'>Dungeons</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <table>
          <tbody>
            {withSortedSpecs.map((character, i) => (
              <tr key={i}>
                <td>
                  <div className='flex items-center gap-1.5 py-px'>
                    <CharacterClassIcon characterClass={character.class} />
                    <Link
                      to={`${team.path}/loot/characters/${character.id}`}
                      className='hover:underline'
                    >
                      {character.name}
                    </Link>
                  </div>
                </td>
                {character.specs.map((spec, i) => {
                  return (
                    <td key={i}>
                      <div className='flex items-center gap-2'>
                        <BlizzardIcon iconName={spec.icon} />
                        {React.createElement(
                          spec.report_url ? `a` : `span`,
                          {
                            className: `${spec.report_url ? 'underline' : ''} ${
                              spec.age
                                ? spec.difference > 21
                                  ? 'text-destructive-foreground'
                                  : spec.difference > 7
                                  ? 'text-warning-foreground'
                                  : 'text-success-foreground'
                                : 'opacity-50 italic'
                            }`,
                            href: spec.report_url,
                            target: '_blank',
                            rel: 'noreferrer',
                          },
                          spec.age ? dayjs(spec.age).fromNow() : 'never',
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  )
}

export default TeamAgeModal
